import dynamic from 'next/dynamic';

import type { ProductHeroBannerProps } from '@/components/sections/einstein/product/banner';
import type { ProductEngineProps } from '@/components/sections/einstein/product/engine';
import type { ProductExploreCarouselProps } from '@/components/sections/einstein/product/explore-carousel';
import type { ProductLineupProps } from '@/components/sections/einstein/product/lineup';
import type { ModalPlatformProps } from '@/components/sections/einstein/product/modal-platform';
import type { ModalProductProps } from '@/components/sections/einstein/product/modal-product';
import type { IMY24ProductNavigation } from '@/components/sections/my24/product/navigation';
import type {
  UspDetailsComponentDataType,
  UspDetailType,
} from '@/components/shared/einstein/usp-popup/index';
import type { ProductCustomizeProps } from '@/components/shared/product-customize';
import { getCta } from '@/contents/lcv/product/maxforce/shared/cta';
import {
  ENGINE_PRODUCT_ITEMS,
  SHARED_PRODUCT_ENGINE_DATA,
} from '@/contents/lcv/product/maxforce/shared/engine';
import { SHARED_PRODUCT_GEAR_DATA } from '@/contents/lcv/product/maxforce/shared/gear';
import {
  getModelLineupOrderByModel,
  PRODUCT_LINEUP_DATA,
  SPARK_DATA,
} from '@/contents/lcv/product/maxforce/shared/lineup';

export const DATA = {
  META_TAG_DATA: {
    title:
      'ใหม่! อีซูซุ ดีแมคซ์​ สปาร์ค New! Isuzu D-Max - อีซูซุประเทศไทย Isuzu Thailand Official',
    description:
      'ยอดปิกอัพอนาคตแห่งการขนส่ง พลังแรง กับเครื่องยนต์ตระกูลใหม่! ISUZU Ddi MAXFORCE ขนาด 2.2 และ 3.0 ลิตร เทคโนโลยีดีเซลส์สุดล้ำ สมบูรณ์แบบเพื่องานโลจิสติกส์ และงานบรรทุกหนัก',
    img: {
      url: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt7476a82c7cfaef2f/67372fcba05f2008342f4b3c/spark.jpg',
    },
  },
  PRODUCT_HERO_BANNER_DATA: {
    active: true,
    menu: {
      hash: '#',
      label: 'ภาพรวม',
    },
    model: 'spark',
    item: {
      description:
        'ใหม่! อีซูซุ ดีแมคซ์ สปาร์ค <span>พลังใหม่...กำหนดโลก!</span>',
      image: {
        src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt6c44a797500eabf0/6735b9b6cc42513c29a24b10/spark-mobile-banner.jpg',
        alt: 'NEW ISUZU SPARK',
      },
      imageDesktop: {
        src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt6e11a952eb025010/673b0454b35d6644b36d8a5b/spark-desktop-banner.jpg',
        alt: 'NEW ISUZU SPARK',
      },
      logo: {
        src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt314f4bd4c55e7e9f/6737388e8a260cf6afa5de72/logo-new-isuzu-d-max-spark.svg',
        alt: 'NEW ISUZU D-MAX SPARK',
        width: 320,
        height: 80,
      },
      buttons: [
        {
          label: 'ข้อเสนอพิเศษ',
          url: '/specialoffer/pup/spark-1-9-financial',
          border: true,
          isMinWidth: true,
          color: 'button-white',
        },
      ],
    },
    gradientColor: '0, 62, 108',
  } as ProductHeroBannerProps,
  PRODUCT_ENGINE_DATA: {
    ...SHARED_PRODUCT_ENGINE_DATA,
    engines: [
      {
        ...ENGINE_PRODUCT_ITEMS[0],
        description: `เครื่องยนต์ดีเซลแห่งอนาคต ความจุ 2.2 ลิตร <span>เหมาะสมยิ่งขึ้น</span>ด้วยเทคโนโลยีดีเซลสำหรับอนาคต <span>ให้กำลังแรงขึ้น</span> ออกตัวเร็วยิ่งขึ้น แต่ลดอัตรา<span>สิ้นเปลือง</span>น้ำมัน<span>และค่ามลพิษ</span>ได้อย่าง<span>ยอดเยี่ยม</span><span>แบบฉบับอีซูซุ</span>`,
      },
      {
        ...ENGINE_PRODUCT_ITEMS[1],
        description: `เครื่องยนต์อีซูซุ 3.0 Ddi MAXFORCE พร้อมระบบเทอร์โบ<span>แปรผัน</span> แรงจัด<span>ตั้งแต่รอบต่ำ</span> <span>ตอบสนอง</span><span>ได้อย่างรวดเร็ว</span> ทนทาน <span>และประหยัด</span><span>น้ำมัน</span>`,
      },
    ],
  } as ProductEngineProps,
  SHARED_PRODUCT_GEAR_DATA: {
    ...SHARED_PRODUCT_GEAR_DATA,
    gearImage: {
      ...SHARED_PRODUCT_GEAR_DATA.gearImage,
      src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt553acac72ad91921/673c18af9cb794980fa2f22a/gear-spark.jpg',
    },
  },
  PRODUCT_EXPLORE_DATA: {
    active: true,
    menu: {
      hash: '#explore',
      label: 'ทำความรู้จักกับ สปาร์ค',
    },
    theme: 'dark',
    model: 'spark',
    section: 'explore',
    headline: {
      preTitle: 'ทำความรู้จักกับ สปาร์ค',
      title: 'Explore Spark',
    },
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt1ae0b1f8b8366145/6736ed7b22e1d8923c62a695/spark-1-exterior.jpg',
          alt: 'Exterior Design',
          width: 864,
          height: 576,
        },
        preTitle: 'ดีไซน์ภายนอก',
        title: 'Exterior Design',
        menu: 'exterior',
        url: '/exterior',
        cardColor: '#0A303B',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt7a96c7e5462908cf/6736ed7b34c0395d8c023345/spark-2-interior.jpg',
          alt: 'Interior Design',
          width: 864,
          height: 576,
        },
        preTitle: 'ดีไซน์ภายใน',
        title: 'Interior Design',
        menu: 'interior',
        url: '/interior',
        cardColor: '#1B1B1B',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt5f2fb42d6f7b28a9/6736ed7b8581947f410dfbcb/spark-3-power.jpg',
          alt: 'Power',
          width: 864,
          height: 576,
        },
        preTitle: 'เครื่องยนต์และระบบส่งกำลัง',
        title: 'Power',
        menu: 'power',
        url: '/power',
        cardColor: '#575345',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb49210ad3244cd2e/6736ed7b8d62f147d868705b/spark-4-safety.jpg',
          alt: 'Safety',
          width: 864,
          height: 576,
        },
        preTitle: 'ระบบความปลอดภัย',
        title: 'Safety',
        menu: 'safety',
        url: '/safety',
        cardColor: '#0B2330',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltaf73496d6cbfe5c7/6736ed7b985b9c64c0621b99/spark-5-platform.jpg',
          alt: 'Platform',
          width: 864,
          height: 576,
        },
        preTitle: 'สมรรถนะ',
        title: 'Platform',
        menu: 'platform',
        url: '/platform',
        cardColor: '#89534E',
      },
    ],
  } as ProductExploreCarouselProps,
  PRODUCT_EXTERIOR_DATA: {
    theme: 'dark',
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltac7b98e8dcb2cb2b/672b12834469ca9ec87ba7cf/1-grille-spark.jpg',
          alt: '3-Dimension Front Grille',
        },
        title: 'กระจังหน้าแบบ <span>3-Dimension</span>',
        description: '<p>ดุดันสะกดทุกสายตา</p>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltf2e6f9b8e88c86fa/672b128265b6a0ae7aaf5faf/2-back-spark.jpg',
          alt: 'Spacious Truck Bed',
        },
        title: 'กระบะท้ายกว้างใหญ่ พร้อมรับทุกงานหนัก',
        description:
          '<p>บรรทุกเยอะ บรรทุกหนักแค่ไหน ก็รับไหว ด้วยกระบะท้าย<span>ขนาดใหญ่</span> กว้างขวาง <span>รองรับรูปแบบ</span>การบรรทุกที่<span>หลากหลาย</span><span>ได้อย่างคุ้มค่า</span> เพื่อสร้างผลกำไร<span>ให้ธุรกิจคุณ</span></p>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt10ef93e4d86b71d3/672b12828c3f95b6d14af72c/3-air-curtain-spark.jpg',
          alt: 'Integrated Design Air Curtain',
        },
        title: 'Air Curtain แบบ Integrated Design',
        description:
          '<p>นวัตกรรม Aerodynamic ลดแรงต้านอากาศ <span>แบบฉบับรถสปอร์ตหรู</span></p>',
      },
    ],
  } as ModalProductProps,
  PRODUCT_INTERIOR_DATA: {
    theme: 'dark',
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltc533427fc18b034e/651f726a6065f3139322953f/usp-interior-spark-mobile-1.jpeg',
          alt: '8-Inch Touchscreen Infotainment Display',
        },
        title: 'หน้าจอระบบสัมผัส Infotainment Display <span>ขนาด 8 นิ้ว</span>',
        description: `<p>ใช้งานง่าย รองรับระบบ Wireless Android Auto<sup>*</sup> และ Wireless Apple Carplay<sup>*</sup></p>
        <p><small><sup>*</sup>เฉพาะสมาร์ทโฟนรุ่นที่รองรับการใช้งาน</small></p>`,
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt4c96fb85535b5b9d/6527a714a4cac261f4c5ce78/usp-interior-spark-mobile-2.jpg',
          alt: 'Spacious Cabin Interior',
        },
        title: 'พื้นที่ห้องโดยสารกว้างขวาง',
        description: '<p>รวมถึงพื้นที่เหนือศีรษะ และพื้นที่วางเท้า</p>',
      },
      // {
      //   image: {
      //     src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltad41ff4a19f14a65/62c79a8ff1696e3810a02775/spark-interior-2-seatadjust-modal.jpeg',
      //     alt: 'Power Windows',
      //   },
      //   title: 'กระจกไฟฟ้า',
      //   description: '<p>เพิ่มความสะดวกสบาย</p>',
      // },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt5682b6d758443aab/62c79a8fe1e8cc357ddc0c44/spark-interior-3-cubbottleslot-modal.jpeg',
          alt: 'Conveniently Positioned Cup Holders',
        },
        title: 'ตำแหน่งวางแก้วใช้งานสะดวก',
        description:
          '<p>พร้อมช่องใส่ขวดน้ำขนาดใหญ่ 1.5 ลิตร <span>ข้างประตู</span> และช่องเก็บของอเนกประสงค์<span>หลายตำแหน่ง</span></p>',
      },
    ],
  } as ModalProductProps,
  PRODUCT_POWER_DATA: {
    theme: 'dark',
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltdf67286edc9bdb6d/6732e530e71e49cb97f0faef/engine-2-2.jpg',
          alt: 'New! ISUZU 2.2 Ddi MAXFORCE',
        },
        title: 'ใหม่! ISUZU 2.2 Ddi MAXFORCE',
        description: `<p>เครื่องยนต์ ISUZU 2.2 Ddi MAXFORCE <span>พลังใหม่ล่าสุดจากอีซูซุ</span> <span>กำลังสูงสุด</span> <span>163 แรงม้า</span> <span>ที่ 3,600 รอบ/นาที</span> <span>แรงบิดสูงสุด</span> <span>400 นิวตัน-เมตร</span> <span>ที่ 1,600 - 2,400 รอบ/นาที</span></p>`,
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltc377d6863a1be52f/673bfece22e1d8380e62b463/engine3-0.jpg',
          alt: 'ISUZU 3.0 Ddi MAXFORCE',
        },
        title: 'ISUZU 3.0 Ddi MAXFORCE',
        description: `<p>เครื่องยนต์ ISUZU 3.0 <span>Ddi MAXFORCE</span> <span>ที่สุดแห่งพลัง</span>
        <span>แรงสะใจจากอีซูซุ</span> <span>กำลังสูงสุด</span> <span>190 แรงม้า</span> <span>ที่ 3,600 รอบ/นาที</span>
        <span>แรงบิดสูงสุด</span> <span>450 นิวตัน-เมตร</span> <span>ที่ 1,600-2,600 รอบ/นาที</span></p>`,
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt4e3d0d22936b3dba/6523c1526c374851e04da426/spark-at-mobile.jpg',
          alt: '8-Speed Automatic Transmission',
        },
        title: 'เกียร์อัตโนมัติ 8 สปีด',
        description: `<p>พร้อมโหมดการขับขี่แบบสปอร์ต <span>Rev Tronic</span> และ <span>Sequential Paddle Shift</span> <span>ที่พวงมาลัย</span> ออกแบบ<span>อัตราทด</span><span>ให้ส่งกำลัง</span>ต่อเนื่อง สัมพันธ์กับแรงบิด<span>ที่สูง</span><span>ตั้งแต่ช่วงออกตัว</span><span>และขณะ</span><span>เร่งแซง</span></p>`,
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb6566a4888f3da1c/62bd0796714a814e575a1d04/spark-drivetrain-2-1MT6speed-modal-2.jpeg',
          alt: '6-Speed Manual Transmission',
        },
        title: 'เกียร์ธรรมดา 6 สปีด',
        description: `<p>แบบ GENIUS SPORT SHIFT อัตราทดใหม่<span>พร้อมระบบแจ้งเตือน</span>การเปลี่ยนเกียร์<span>ในรอบ</span>ความเร็ว<span>ที่เหมาะสม</span> ออกตัวดีเยี่ยม อัตราเร่งแรงเร้าใจ</p>`,
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt5db516420188a249/651646bc2222f13d8d123121/usp-power-spark-modal-5.jpg',
          alt: 'Rough Terrain Mode System',
        },
        title: 'ระบบ Rough Terrain Mode',
        description:
          '<p>ช่วยเพิ่มประสิทธิภาพในการขับขี่ทั้ง 2 และ <spark>4 ล้อ</spark> ด้วยการควบคุม<span>การกระจายกําลัง</span><span>ทุกช่วงความเร็ว</span> เพื่อให้ฝ่าอุปสรรคไปได้อย่างง่ายดาย</p>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt6f1abba88ff1daa9/651fbca896bebfbb8da06184/usp-power-spark-modal-6.jpeg',
          alt: 'New! Climb Angle, Slope, and Wheel Steering Direction Display System',
        },
        title:
          'ระบบแสดงองศามุมปีนไต่ ลาดเอียง และ <span>ทิศทางการเลี้ยวของล้อ</span>',
        description:
          '<p>แสดงผลที่หน้าจอ Infotainment Display <span>ลุยได้อย่างมั่นใจยิ่งขึ้น</span></p>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt7427c65152e139e1/6512a713549cbdf525b9dac6/usp-power-spark-modal-8.jpg',
          alt: 'Electronic Diff-Lock System',
        },

        title: 'ระบบ Electronic Diff-Lock',
        description:
          '<p>ช่วยล็อกเฟืองท้ายด้วยไฟฟ้า ให้เครื่องยนต์ส่งกำลังไปยังล้อหลังด้านซ้าย <span>และขวาเท่ากัน</span> ให้ทุกอุปสรรคเป็นเรื่องง่าย</p>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt221c8705816a0d82/6512a713474d4ddc014f306e/usp-power-spark-modal-7.jpg',
          alt: 'Terrain Command System',
        },
        title: 'ระบบ Terrain Command',
        description:
          '<p>สวิตช์เปลี่ยนการขับเคลื่อนจาก 2 ล้อ<span>เป็น 4 ล้อ</span> <span>เลือกการใช้งาน</span><span>ให้เหมาะกับทุกสภาพถนน</span><span>ทั้ง 2H/4H และ 4L</span></p>',
      },
    ],
  } as ModalProductProps,
  PRODUCT_SAFETY_DATA: {
    theme: 'dark',
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt107fee9c964cecf4/62bd07b2c10bb24d85245437/spark-safety-2-1ABS-EBD-BA-modal.jpeg',
          alt: 'ABS / EBD / BA',
        },
        title: 'ABS / EBD / BA',
        description:
          '<ul><li>ระบบป้องกันล้อล็อกขณะเบรกแบบอิเล็กทรอนิกส์ ABS (Anti-Lock Brake System) ชนิด <span>4-Channel</span> <span>4-Sensor</span></li><li>ระบบช่วยกระจายแรงเบรกให้สัมพันธ์กับน้ำหนักบรรทุกของรถ EBD (Electronic Brake-force Distribution)</li><li>ระบบช่วยเพิ่มแรงเบรกอัตโนมัติ เมื่อเบรกกะทันหัน BA (Brake Assist)</li></ul>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb98ea8b61a729e70/6513e46523b7e7ee9e6a9bbc/USP-4DOORS-MOBILE-PROTECTION-7.jpg',
          alt: 'ESC / TCS',
        },
        title: 'ESC / TCS',
        description:
          '<ul><li>ระบบควบคุมการทรงตัว ESC (Electronic Stability Control)</li><li>ระบบป้องกันล้อหมุนฟรีขณะออกตัว TCS (Traction Control System)</li></ul>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt0c3a63cd7f8e7f72/6513aca18286ce18f50f949a/USP-4DOORS-DESKTOP-PROTECTION-8.jpg',
          alt: 'HSA / HDC',
        },
        title: 'HSA / HDC',
        description:
          '<ul><li>ระบบช่วยออกตัวบนทางลาดชัน HSA (Hill Start Assist)</li><li>ระบบควบคุมความเร็วลงทางลาดชัน HDC (Hill Descent Control)</li></ul>',
      },
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt4a76607c41212478/651f9c143ce1e80d1bfffbe4/usp-spark-safety-air-bag.jpg',
          alt: 'Dual SRS Airbags',
        },
        title: 'Dual SRS Airbags',
        description:
          '<p>แอร์แบคคู่หน้า <span>พร้อม Crash Unlock</span> <span>ปลดล็อกประตูอัตโนมัติ</span> <span>เมื่อแอร์แบคทำงาน</span></p>',
      },
    ],
  } as ModalProductProps,
  PRODUCT_PLATFORM_DATA: {
    theme: 'dark',
    items: [
      {
        image: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blta8abade4098f8b54/672de434c09b5d6676c4bb12/platform.jpg',
          alt: 'Platform',
          width: 1440,
          height: 960,
        },
        imageDesktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltff06b7d5bbc271f8/672de421e5efba6c76e06aa2/platform-desktop.jpg',
          alt: 'Platform',
          width: 1680,
          height: 1260,
        },
        title: 'ISUZU Dynamic Drive Platform',
        items: [
          {
            title: 'Unlock The Stability',
            items: [
              {
                title: 'Dynamic Drive Platform',
                description:
                  '<p>โครงสร้างตัวถัง ที่ผสานทุกการขับขี่ เกินคำว่าสมบูรณ์แบบ</p>',
              },
              // {
              //   title: 'Semi-Midship',
              //   description:
              //     '<p>การวางตำแหน่งเครื่องยนต์เยื้องหลังเพลาหน้า เพื่อกระจายน้ำหนักได้สมดุล</p>',
              // },
            ],
          },
          {
            title: 'Unlock The Durability',
            items: [
              // {
              //   title: 'Melt-in Liner',
              //   description:
              //     '<p>เสื้อสูบชุบแข็งแบบเหนี่ยวนำด้วยคลื่นความถี่สูง เอกลักษณ์พิเศษเฉพาะอีซูซุ</p>',
              // },
              {
                title: 'Ultra-High Tensile',
                description:
                  '<p>โครงสร้างตัวถังทนทานต่อการบิดตัว แข็งกว่าเหล็กธรรมดา</p>',
              },
              {
                title: 'Timing Gear & Chain',
                description:
                  '<p>ระบบปรับความตึงอัตโนมัติ ทำให้เครื่องยนต์ทนทานและเงียบ</p>',
              },
            ],
          },
        ],
      },
    ],
  } as ModalPlatformProps,
  PRODUCT_CUSTOMIZE_DATA: {
    theme: 'dark',
    menu: {
      hash: '#customize',
      label: 'สำรวจรถของคุณ',
    },
    active: true,
    model: 'spark',
    headline: {
      preTitle: 'สำรวจรถของคุณ',
      title: 'Future of Your Choice',
    },
  } as ProductCustomizeProps,
  PRODUCT_NAVIGATION_DATA: {
    active: true,
    theme: 'dark',
    initialSectionIndex: 1,
    isTransparent: true,
    title: SPARK_DATA.label,
    price: SPARK_DATA.price,
    buttons: [
      {
        label: 'คำนวณค่างวด',
        url: '/calculator?model=spark',
        color: 'button-white',
        size: 'md',
        isMinWidth: true,
        border: true,
      },
      {
        label: 'สนใจรถ',
        color: 'button-white',
        size: 'md',
        isMinWidth: true,
      },
    ],
  } as IMY24ProductNavigation,
  PRODUCT_LINEUP_DATA: {
    menu: {
      hash: '#lineup',
      label: 'รุ่นรถอีซูซุ',
    },
    headline: {
      preTitle: 'รุ่นรถอีซูซุ',
      title: 'Model Lineup',
    },
    items: getModelLineupOrderByModel('spark', PRODUCT_LINEUP_DATA),
    dataTrack: {
      dataTrack: 'lcv-product',
      dataTrackSection: 'lineup',
    },
  } as ProductLineupProps,
  PRODUCT_CTA_DATA: {
    ...getCta('spark'),
  },
};

export const USP_DETAILS_ORDERED_KEYS: UspDetailType[] = [
  'exterior',
  'interior',
  'power',
  'safety',
  'platform',
];

export const USP_DETAILS_COMPONENTS: UspDetailsComponentDataType = {
  exterior: {
    key: 'exterior',
    metatag: {
      description: `NEW! ISUZU D-MAX SPARK 4x2 / SPARK 4x4 ยอดปิกอัพอนาคตการขนส่งพลังแรง กับเครื่องยนต์ดีเซลแห่งอนาคตสมบูรณ์แบบเพื่องานโลจิสติกส์ และงานบรรทุกหนัก`,
    },
    item: {
      ...DATA.PRODUCT_EXPLORE_DATA.items[0],
      title: 'Future of Logistics',
      description: `<p>NEW! ISUZU D-MAX SPARK 4x2 <span>/ SPARK 4x4</span> <span>ยอดปิกอัพอนาคต</span>การขนส่ง<span>พลังแรง</span><span>กับเครื่องยนต์ดีเซล</span><span>แห่งอนาคต</span><span>สมบูรณ์แบบ</span>เพื่องาน<span>โลจิสติกส์</span> <span>และงานบรรทุกหนัก</span></p>`,
      componentProps: DATA.PRODUCT_EXTERIOR_DATA,
      headerImage: {
        desktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt39ee33ed3d9e6896/6735ba25af3729314703adb2/spark-desktop-exterior.jpg',
          alt: 'Future of Logistics',
          width: 1280,
          height: 720,
        },
        mobile: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/bltb2fb6bdcc63af5a8/6735ba38e5efba4306e0868e/spark-mobile-exterior.jpg',
          alt: 'Future of Logistics',
          width: 360,
          height: 640,
        },
      },
    },
    order: 0,
    Component: dynamic(
      () => import('@/components/sections/einstein/product/modal-product'),
      {
        ssr: false,
      }
    ),
  },
  interior: {
    key: 'interior',
    metatag: {
      description: `ออกแบบห้องโดยสารภายในให้ตอบโจทย์การขับขี่ทั้งระยะทางใกล้และไกลเน้นความสะดวกสบาย พร้อมเบาะนั่งออกแบบรองรับทุกสรีระ ลดอาการเมื่อยล้าสำหรับทุกสภาพถนนแม้ต้องขับขี่เป็นเวลานาน`,
    },
    item: {
      ...DATA.PRODUCT_EXPLORE_DATA.items[1],
      title: 'Future of Comfort',
      description: `<p>ออกแบบห้องโดยสารภายในให้ตอบโจทย์การขับขี่ทั้งระยะทางใกล้และไกล <span>เน้นความสะดวกสบาย</span> พร้อมเบาะนั่งออกแบบรองรับทุกสรีระ <span>ลดอาการเมื่อยล้าสำหรับทุกสภาพถนน</span><span>แม้ต้องขับขี่เป็นเวลานาน</span></p>`,
      componentProps: DATA.PRODUCT_INTERIOR_DATA,
      headerImage: {
        desktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt095ffeb6966c7497/6731ba51ed5a1d3bb6e34427/spark-desktop-interior.jpg',
          alt: 'Future of Comfort',
          width: 1280,
          height: 720,
        },
        mobile: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt4382de47a392a688/6731ba5153e3c458e3b3b3d6/spark-mobile-interior.jpg',
          alt: 'Future of Comfort',
          width: 360,
          height: 640,
        },
      },
    },
    order: 1,
    Component: dynamic(
      () => import('@/components/sections/einstein/product/modal-product'),
      {
        ssr: false,
      }
    ),
  },
  power: {
    key: 'power',
    metatag: {
      description: `ยอดปิกอัพอนาคตการขนส่งพลังแรง กับเครื่องยนต์ดีเซลแห่งอนาคต ISUZU 2.2 Ddi MAXFORCE และ เครื่องยนต์ 3.0 Ddi MAXFORCE สมบูรณ์แบบเพื่องานโลจิสติกส์ และงานบรรทุกหนัก`,
    },
    item: {
      ...DATA.PRODUCT_EXPLORE_DATA.items[2],
      title: 'Future of Power',
      description: `<p>ยอดปิกอัพอนาคตการขนส่งพลังแรง <span>กับเครื่องยนต์ดีเซล</span>แห่งอนาคต <span>ISUZU 2.2 Ddi MAXFORCE</span> และ เครื่องยนต์ 3.0 Ddi MAXFORCE สมบูรณ์แบบ<span>เพื่องานโลจิสติกส์</span> <span>และงานบรรทุกหนัก</span></p>`,
      componentProps: DATA.PRODUCT_POWER_DATA,
      headerImage: {
        desktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt0ca3ba269d75d675/6735ba5b4f4fa3530db7bbf6/spark-desktop-power.jpg',
          alt: 'Future of Power',
          width: 1280,
          height: 720,
        },
        mobile: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt6897781495b8d4f1/6735ba6faecb493e262a7bc5/spark-mobile-power.jpg',
          alt: 'Future of Power',
          width: 360,
          height: 640,
        },
      },
    },
    order: 2,
    Component: dynamic(
      () => import('@/components/sections/einstein/product/modal-product'),
      {
        ssr: false,
      }
    ),
  },
  safety: {
    key: 'safety',
    metatag: {
      description: `ทุกเส้นทางอุ่นใจ ด้วยเทคโนโลยีระบบความปลอดภัยล่าสุด พร้อมเทคโนโลยีระบบความปลอดภัยเต็มรูปแบบ พร้อมพาคุณลุยในทุกเส้นทางอย่างมั่นใจได้เต็มที่`,
    },
    item: {
      ...DATA.PRODUCT_EXPLORE_DATA.items[3],
      title: 'Future of Safety',
      description: `<p>ทุกเส้นทางอุ่นใจ ด้วยเทคโนโลยี<span>ระบบความปลอดภัย</span>ล่าสุด พร้อมเทคโนโลยี<span>ระบบความปลอดภัย</span>เต็มรูปแบบ <span>พร้อมพาคุณลุย</span><span>ในทุกเส้นทาง</span>อย่างมั่นใจได้เต็มที่</p>`,
      componentProps: DATA.PRODUCT_SAFETY_DATA,
      headerImage: {
        desktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt19bc7de5f7cbc165/6735bca5a3ae0e709f197a1f/spark-desktop-safety.jpg',
          alt: 'Future of Safety',
          width: 1280,
          height: 720,
        },
        mobile: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt1d87390e825f48f7/6735bcb44ef1044348e56fa9/spark-mobile-safety.jpg',
          alt: 'Future of Safety',
          width: 360,
          height: 640,
        },
      },
    },
    order: 3,
    Component: dynamic(
      () => import('@/components/sections/einstein/product/modal-product'),
      {
        ssr: false,
      }
    ),
  },
  platform: {
    key: 'platform',
    metatag: {
      description: `บรรทุกหนักเต็มสมรรถนะ พร้อมลุยได้เต็มที่ กับ “ISUZU DYNAMIC DRIVE PLATFORM” เอกลักษณ์การออกแบบที่ผสานการทำงานของโครงสร้างตัวถัง แชสซีส์ เครื่องยนต์ และช่วงล่างให้ทำงานร่วมกันเป็นหนึ่ง มั่นใจทุกสภาพถนน`,
    },
    item: {
      ...DATA.PRODUCT_EXPLORE_DATA.items[4],
      title: 'Future of Platform',
      description: `<p>บรรทุกหนักเต็มสมรรถนะ พร้อมลุยได้เต็มที่ กับ “ISUZU DYNAMIC DRIVE
PLATFORM” เอกลักษณ์การออกแบบ<span>ที่ผสาน</span>การทำงานของ<span>โครงสร้างตัวถัง</span>
<span>แชสซีส์</span> <span>เครื่องยนต์</span> <span>และช่วงล่าง</span>ให้ทำงานร่วมกันเป็นหนึ่ง <span>มั่นใจทุกสภาพถนน</span></p>`,
      componentProps: DATA.PRODUCT_PLATFORM_DATA,
      headerImage: {
        desktop: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt3a89e2ef89aa5cd0/6735bcca86ddd24e7924df29/spark-desktop-platform.jpg',
          alt: 'Future of Platform',
          width: 1280,
          height: 720,
        },
        mobile: {
          src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt0ff7c866b79bd3ca/6735bce220ed6c3125a52930/spark-mobile-platform.jpg',
          alt: 'Future of Platform',
          width: 360,
          height: 640,
        },
      },
    },
    order: 4,
    Component: dynamic(
      () => import('@/components/sections/einstein/product/modal-platform'),
      {
        ssr: false,
      }
    ),
    customizeItem: {
      image: {
        src: 'https://images.contentstack.io/v3/assets/blt04fb7dc282801dd0/blt711f8d971ebe3741/6732df25e71e4923f2f0fad4/spark-4x2-front-s-elbrus-gray-opaque.png',
        alt: 'ISUZU SPARK',
        width: 768,
        height: 472,
      },
      title: 'CUSTOMIZE',
    },
  },
};
